import { useMutation } from '@tanstack/react-query';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { ResendVerificationForm } from '@/features/resend-verification';
import { useTRPC } from '@/shared/api';
import { Notice } from '@/shared/ui/notice';

/** 서버가 준 문구가 없을 때 보여줄 말. 영어 원문(`Failed to fetch`)을 그대로 띄우지 않는다. */
const FALLBACK_MESSAGE = '인증하지 못했습니다. 잠시 후 다시 시도해 주세요.';

type Outcome = { kind: 'working' } | { kind: 'done' } | { kind: 'failed'; message: string };

const toMessage = (error: unknown): string =>
  error instanceof Error && error.message.length > 0 ? error.message : FALLBACK_MESSAGE;

/**
 * 이메일 인증 착지 화면 (`/verify?token=...`).
 *
 * 메일 링크가 여기로 온다. 토큰 소비는 **이 화면이 auth.verifyEmail 을 부를 때** 일어난다 —
 * 백엔드 GET 으로 두면 메일 보안 스캐너가 링크를 미리 열어 토큰이 타 버린다.
 *
 * `/auth` 아래에 두지 않는다 — 그 접두사는 소셜 라우트가 쓰고 프록시가 통째로 api 로
 * 넘긴다(`webpack.config.js`·`nginx.conf.template`).
 */
export function VerifyEmailPage(): ReactElement {
  const [params] = useSearchParams();
  const token = params.get('token');
  const trpc = useTRPC();
  const { mutateAsync } = useMutation(trpc.auth.verifyEmail.mutationOptions());

  /**
   * 결과를 `useMutation` 의 상태가 아니라 **여기에** 담는다.
   *
   * 실제 브라우저에서 화면이 "확인하는 중"에 영원히 멈추는 것을 재현했다. 링크는 응답을
   * 정상적으로 올려보내는데(`next`·`complete` 를 로그로 확인) 뮤테이션 상태만 `pending` 에
   * 머물렀다 — StrictMode 가 effect 를 두 번 돌리는 사이 관찰자 구독이 끊겼다 붙는데,
   * 그 틈에 끝난 결과를 못 받는 자리가 있다. 앱 부팅의 `auth.refresh` 와 한 묶음으로
   * 나가면서(httpBatchLink) 타이밍이 맞아떨어졌다.
   *
   * `mutateAsync` 가 돌려주는 약속은 구독과 무관하게 끝나므로 그 결과로 화면을 정한다.
   * **단위 테스트로는 잡히지 않았다** — jest 는 통과했고 실제 브라우저에서만 났다.
   */
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'working' });

  /**
   * **반드시 한 번만 보낸다.** 토큰 소비는 미사용 조건이 WHERE 에 있는 UPDATE 한 문장이라
   * 두 번째 요청은 무조건 실패한다. 막지 않으면 성공한 인증이 "만료된 링크"로 보인다.
   */
  const isRequested = useRef(false);

  useEffect(() => {
    if (token === null || isRequested.current) {
      return;
    }
    isRequested.current = true;
    void mutateAsync({ token }).then(
      () => setOutcome({ kind: 'done' }),
      (error: unknown) => setOutcome({ kind: 'failed', message: toMessage(error) }),
    );
  }, [token, mutateAsync]);

  if (token === null) {
    return (
      <Notice title="링크가 올바르지 않습니다">
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          메일에 있는 주소를 그대로 눌러 주세요. 주소가 잘려서 열렸을 수 있습니다.
        </p>
      </Notice>
    );
  }

  if (outcome.kind === 'done') {
    return (
      <Notice title="이메일 인증이 완료되었습니다">
        <p className="text-[14px] leading-[1.8] text-muted-foreground">
          이제 로그인하실 수 있습니다.
        </p>
        <Link
          to="/login"
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/85"
        >
          로그인하러 가기
        </Link>
      </Notice>
    );
  }

  if (outcome.kind === 'failed') {
    return (
      <Notice title="인증하지 못했습니다">
        <p className="text-[14px] leading-[1.8] text-destructive">{outcome.message}</p>
        <ResendVerificationForm />
      </Notice>
    );
  }

  return (
    <Notice title="이메일을 확인하는 중입니다">
      <p className="text-[14px] leading-[1.8] text-muted-foreground">잠시만 기다려 주세요.</p>
    </Notice>
  );
}
